import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './PlanPreviewDemoAgentic.module.css';

interface Step {
  id: number;
  text: string;
  marker?: string;
}

const PLAN: Step[] = [
  { id: 1, text: 'Create accounts (email, Slack, Figma)' },
  { id: 2, text: 'Add Ana to #design and #general' },
  { id: 3, text: 'Schedule an intro with her team lead', marker: 'Involves 1 person' },
  { id: 4, text: 'Send a welcome email', marker: "Can't be undone" },
];

const STEP_MS = 2000;
const CHANGE_STEP = 3;
const HELPER = 'Remove a step or stop mid-run to see what the agent reports.';

type Phase = 'plan' | 'running' | 'paused' | 'stopped' | 'done';

export function PlanPreviewDemoAgentic() {
  const [steps, setSteps] = useState<Step[]>(PLAN);
  const [phase, setPhase] = useState<Phase>('plan');
  // Index into `steps` of the step currently running
  const [current, setCurrent] = useState(0);
  const timer = useRef<number | undefined>(undefined);

  // Advance one step every STEP_MS while running
  useEffect(() => {
    if (phase !== 'running') return;
    timer.current = window.setTimeout(() => {
      if (current + 1 >= steps.length) {
        setCurrent(steps.length);
        setPhase('done');
      } else {
        setCurrent((c) => c + 1);
      }
    }, STEP_MS);
    return () => window.clearTimeout(timer.current);
  }, [phase, current, steps.length]);

  const started = phase !== 'plan';
  const changeReached = started && steps.some((s, i) => s.id === CHANGE_STEP && i <= current);
  const doneSteps = steps.slice(0, Math.min(current, steps.length));
  const notStarted = steps.slice(Math.min(current, steps.length));

  const statusOf = (i: number) => {
    if (!started) return 'planned';
    if (i < current) return 'done';
    if (i === current && phase !== 'stopped' && phase !== 'done') return 'now';
    return 'next';
  };

  const label = (step: Step) =>
    step.id === CHANGE_STEP && changeReached ? 'Schedule an intro with her team lead - Tuesday 10:00' : step.text;
  const list = (items: Step[]) => (items.length ? items.map((s) => s.id).join(', ') : 'none');

  let feedback = HELPER;
  if (phase === 'running') feedback = 'Running one step at a time. Pause or stop whenever you like.';
  if (phase === 'paused') feedback = 'Paused. Nothing else happens until you resume or stop.';
  if (phase === 'stopped') feedback = 'Stopped. The agent reports exactly what finished and what never started.';
  if (phase === 'done') feedback = 'Finished. The one change from the plan was reported while it happened, not after.';

  return (
    <div className={kit.root}>
      <div className={kit.message}>Set up Ana for her first day on Monday.</div>

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>{started ? "Ana's first day - running" : "Plan - Ana's first day"}</span>
          <span>
            {steps.length} {steps.length === 1 ? 'step' : 'steps'}
          </span>
        </div>

        <div className={`${kit.log} ${styles.steps}`}>
          {steps.map((step, i) => {
            const status = statusOf(i);
            return (
              <div key={step.id} className={`${kit.logRow} ${styles.step}`}>
                <span className={kit.logTime}>{String(step.id).padStart(2, '0')}</span>
                <span
                  className={`${kit.logDot} ${
                    status === 'done' ? kit.logDotDone : status === 'now' && phase === 'running' ? kit.logDotActive : ''
                  }`}
                  aria-hidden="true"
                />
                <div className={styles.stepBody}>
                  <span className={status === 'done' ? styles.doneText : undefined}>{label(step)}</span>
                  {step.marker && <span className={`${kit.chip} ${kit.chipWarn} ${styles.marker}`}>{step.marker}</span>}
                </div>
                {started ? (
                  <span className={`${kit.chip} ${status === 'done' ? kit.chipGood : ''}`}>
                    {status === 'done' ? 'Done' : status === 'now' ? 'Now' : phase === 'stopped' ? 'Not started' : 'Next'}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={`${kit.btn} ${kit.btnGhost} ${styles.remove}`}
                    onClick={() => setSteps((prev) => prev.filter((s) => s.id !== step.id))}
                    aria-label={`Remove step: ${step.text}`}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
          {steps.length === 0 && <p className={kit.feedback}>Every step removed - nothing to run.</p>}
        </div>

        {changeReached && (
          <div className={styles.notice} role="status">
            <span className={`${kit.chip} ${kit.chipWarn}`}>Changed from plan</span>
            <span>Team lead is busy Monday, booking Tuesday 10:00 instead.</span>
          </div>
        )}

        {phase === 'stopped' && (
          <div className={styles.summary}>
            <span className={kit.label}>Stopped</span>
            <p>
              Done: {list(doneSteps)}. Not started: {list(notStarted)}.
            </p>
          </div>
        )}

        <div className={`${kit.row} ${styles.controls}`}>
          {phase === 'plan' && (
            <button
              type="button"
              className={`${kit.btn} ${kit.btnPrimary}`}
              disabled={steps.length === 0}
              onClick={() => setPhase('running')}
            >
              Start
            </button>
          )}
          {phase === 'running' && (
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => setPhase('paused')}>
              Pause
            </button>
          )}
          {phase === 'paused' && (
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setPhase('running')}>
              Resume
            </button>
          )}
          {(phase === 'running' || phase === 'paused') && (
            <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={() => setPhase('stopped')}>
              Stop
            </button>
          )}
        </div>
      </div>

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
